import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';
import {
  TableColumn,
  TableComponent,
} from '../../../../shared/components/table/table.component';
import { ToastType } from '../../../../shared/interfaces/common';
import {
  CustomSubscriptionPlanListItem,
  CustomSubscriptionPlansSummary,
  SubscriptionPlanFilter,
} from '../../models/subscription';
import { CommonService } from '../../../../shared/services/common/common.service';
import { SubscriptionService } from '../../services/subscription.service';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { getApiErrorMessage } from '../../../../shared/utils/utils';

@Component({
  selector: 'app-manage-subscription-plans',
  templateUrl: './manage-subscription-plans.component.html',
  styleUrls: ['./manage-subscription-plans.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePipe,
    PaginationComponent,
    PageSizeComponent,
    SearchInputComponent,
    TableComponent,
    SvgIconComponent,
  ],
})
export class ManageSubscriptionPlansComponent
  extends MenuComponentBase
  implements OnInit
{
  private router = inject(Router);
  private subscriptionService = inject(SubscriptionService);
  private commonService = inject(CommonService);
  private searchSubject = new Subject<string>();

  isLoading = false;
  pageSize = 10;
  currentPage = 1;
  search = '';

  subscriptionPlans: CustomSubscriptionPlanListItem[] = [];
  plansCount = 0;

  summary: CustomSubscriptionPlansSummary = {
    total: 0,
    active: 0,
    archived: 0,
  };

  subPlanFilter: SubscriptionPlanFilter = 'all';

  tableColumns: TableColumn[] = [
    { header: 'Plan Name', width: '25%' },
    { header: 'Status', width: '10%' },
    { header: 'Trial Period', width: '12%' },
    { header: 'Features', width: '15%' },
    { header: 'Connections', width: '15%' },
    { header: 'Last Updated', width: '15%' },
    { header: 'Action', width: '8%' },
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.setupSearchSubscription();

    const planParam = this.route.snapshot.queryParamMap.get('plan');
    if (planParam) {
      this.router.navigate([
        '/configs/manage-subscription-plans/view',
        planParam,
      ]);
      return;
    }

    this.getCustomSubscriptionPlans();
  }

  private setupSearchSubscription(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query: string) => {
        this.search = query;
        this.currentPage = 1;
        this.getCustomSubscriptionPlans();
      });
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.getCustomSubscriptionPlans();
    }
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.getCustomSubscriptionPlans();
  }

  getCustomSubscriptionPlans(): void {
    this.isLoading = true;

    this.subscriptionService
      .getCustomsubscriptionPlans(
        this.currentPage,
        this.pageSize,
        this.search,
        this.subPlanFilter,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.subscriptionPlans = response.results || [];
          this.plansCount = response.count || 0;
          if (response.summary) {
            this.summary = response.summary;
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: getApiErrorMessage(
              error,
              'Failed to load subscription plans.',
            ),
          });
        },
      });
  }

  applyFilter(filter: SubscriptionPlanFilter): void {
    this.subPlanFilter = filter;
    this.currentPage = 1;
    this.getCustomSubscriptionPlans();
  }

  onSearchQueryChange(query: string): void {
    this.searchSubject.next(query);
  }

  onCreatePlan(): void {
    this.router.navigate(['/configs/manage-subscription-plans/create']);
  }

  onPlan(plan: CustomSubscriptionPlanListItem): void {
    this.router.navigate([
      '/configs/manage-subscription-plans/view',
      plan.plan_name,
    ]);
  }
}
